import React, { useEffect, useState } from 'react';

import { FormGroup } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { Select } from '../../../../../components/atoms';
import { Clock as ClockIcon } from '../../../../../components/icons';
import { PickupLocationType } from '../../../../../core/api/restaurant/restaurant.types';
import { AppConfig } from '../../../../../core/api/whitelabel/whitelabel.types';
import { selectRestaurantDetails } from '../../../../../core/state/restaurant/restaurantSelectors';
import { Track } from '../../../../../weborder/analytics';
import { BLANK } from '../../../../../weborder/constants/Constants';
import { checkoutUtils } from '../../../utils';
import { ServiceTypes } from '../../../utils/checkoutUtils';

type Props = {
  changeDeliveryTime: (time: string) => void;
  configData: AppConfig;
  disabled?: boolean;
  pickupLocationType: PickupLocationType;
  requestedDeliveryOrPickupTimeUtc: string;
  restaurant: ReturnType<typeof selectRestaurantDetails>;
  serviceTypes: ServiceTypes;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  pickUpTimeSelector: {
    marginBottom: theme.spacing(2),

    '&:before, &:after': {
      content: 'none',
    },

    '&.MuiInputBase-root': {
      border: `1px solid ${theme.palette.grey[400]}`,
      position: 'relative',

      '& .MuiSelect-select': {
        padding: theme.spacing(1.75, 5.75, 1.75),
      },

      '&.Mui-focused': {
        border: `1px solid ${theme.palette.common.black}`,
      },
      '& .MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd': {
        position: 'absolute',
      },
      '& .MuiInputAdornment-positionStart': {
        left: theme.spacing(1.5),
      },
      '& .fd-icon': {
        color: theme.palette.common.black,
        right: theme.spacing(1),
      },
    },
  },
}));

export function SelectTimeField(props: Props): JSX.Element {
  const {
    configData,
    changeDeliveryTime,
    disabled,
    pickupLocationType,
    requestedDeliveryOrPickupTimeUtc,
    restaurant,
    serviceTypes,
    translate,
  } = props;

  const { isDelivery } = serviceTypes;

  const [storedTime, setStoredTime] = useState('');
  const classes = useStyles();

  useEffect(() => {
    checkoutUtils.forcePreOrderSelection(
      changeDeliveryTime,
      restaurant,
      requestedDeliveryOrPickupTimeUtc,
      pickupLocationType,
      setStoredTime,
    );
  }, [restaurant, requestedDeliveryOrPickupTimeUtc]);

  useEffect(() => {
    changeDeliveryTime(BLANK);
  }, [storedTime]);

  const handleChangeDeliveryTime = (e): void => {
    const getEventLabel = isDelivery
      ? 'Delivery Time Selected'
      : 'Collection Time Selected';
    const isPreorder = restaurant.AcceptPreOrders && !restaurant.IsOpen;
    const selectedTime = e.target.value;

    const eventData = {
      amp_isPreorder: isPreorder,
      amp_label: getEventLabel,
      amp_timeSelected: selectedTime,
      label: 'Change Collection Time',
      section: 'Orders',
    };

    Track('Orders', 'Change Collection Time', eventData);
    changeDeliveryTime(selectedTime);
  };

  if (
    !restaurant ||
    !restaurant.AcceptPreOrders ||
    !restaurant.PreOrderOptions
  ) {
    return null;
  }

  const isPickupDisabled =
    checkoutUtils.getIsDisallowedPreorderAndTableService(
      restaurant,
      pickupLocationType,
    ) || disabled;

  const timeOptions = checkoutUtils.getSelectTimeOptions(
    restaurant,
    configData,
    pickupLocationType,
    translate,
  );

  const orderTypeLabel = isDelivery
    ? translate('CheckoutPreorder_deliveryTime')
    : translate('OrderCompletePage_CollectionTime');

  const selectedOption = timeOptions.find(
    (option) => option.key === requestedDeliveryOrPickupTimeUtc,
  );
  const selectedOrDefaultOption = selectedOption || null;

  return (
    <FormGroup>
      <Select
        aria-label={orderTypeLabel}
        className={classes.pickUpTimeSelector}
        disabled={isPickupDisabled}
        fullWidth
        id="selectTime"
        label={orderTypeLabel}
        menuItems={timeOptions}
        onChange={handleChangeDeliveryTime}
        startIcon={<ClockIcon />}
        selected={selectedOrDefaultOption}
      />
    </FormGroup>
  );
}
